import { goBack } from "assets/icons";
import styled from "@emotion/styled";
import { Card, Col, Form, message, Row, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import MainCreateProduct from "./MainCreateProduct";
import CreateProductFooter from "./CreateProductFooter";
import { useState, useEffect, useRef } from "react";
import { AttributeInput, CreateProductInput } from "services/interface";
import { backendUrl } from "config";
import _ from "lodash";
import { useCreateProduct, useGetProductById } from "hooks/useProduct";
import { useQueryClient } from "@tanstack/react-query";
import { buildCloneFormState } from "../cloneProduct";

interface CreateProductProps {
  /** id สินค้าต้นฉบับ (โหมดคัดลอกสินค้า) — กรอกฟอร์มให้ล่วงหน้า แล้วผู้ใช้กดบันทึกเอง */
  cloneFromId?: string;
}

const CreateProduct = ({ cloneFromId }: CreateProductProps) => {
  const navigate = useNavigate();
  const [attribute, setAttribute] = useState<AttributeInput[]>([]);
  const [variants, setVariants] = useState<VariantsInput>([]);
  const [prices, setPrices] = useState<PricesInput>([]);

  const [checkIsActive, setCheckIsActive] = useState<boolean>(false);
  const [checkIsRecommend, setCheckIsRecommend] = useState<boolean>(false);
  const [checkIsApproved, setCheckIsApproved] = useState<boolean>(false);
  const [checkIsShowApp, setCheckIsShowApp] = useState<boolean>(false);

  const { mutate: CreateProduct, isPending } = useCreateProduct();

  const [form] = Form.useForm();

  const queryClient = useQueryClient();

  // โหมดคัดลอกสินค้า: ดึงสินค้าต้นฉบับมากรอกฟอร์มให้ครั้งเดียว (ยังไม่บันทึกจนผู้ใช้กดบันทึก)
  const { data: cloneSource } = useGetProductById(cloneFromId ?? "");
  const cloneInitializedRef = useRef(false);

  useEffect(() => {
    if (!cloneFromId || !cloneSource || cloneInitializedRef.current) return;
    cloneInitializedRef.current = true;

    const cloned = buildCloneFormState(cloneSource.product);
    setVariants(cloned.variants);
    setPrices(cloned.prices);
    setAttribute(cloned.attribute);
    setCheckIsActive(cloned.fields.is_active);
    setCheckIsRecommend(cloned.fields.is_recommend);
    form.setFieldsValue(cloned.fields);
  }, [cloneFromId, cloneSource, form]);

  const onFinish = (values: any) => {
    // console.log("Success:", values);

    // ✅ บังคับต้องมี SKU เฉพาะเมื่อเปิดการขาย (is_active) — สร้างสินค้าโดยไม่มี SKU ได้
    const requireSKU = values.is_active === true;

    if (
      // _.isEmpty(attribute) ||
      (requireSKU && (_.isEmpty(variants) || _.isEmpty(prices))) ||
      _.isEmpty(values.category_id) ||
      _.isEmpty(values.retail_type)
    ) {
      // if (_.isEmpty(attribute)) {
      //   message.error("กรุณาเพิ่มคุณลักษณะสินค้า");
      // }
      if (requireSKU && _.isEmpty(variants)) {
        message.error("กรุณาเพิ่ม SKU อย่างน้อย 1 รายการก่อนเปิดการขาย");
      }
      if (requireSKU && _.isEmpty(prices)) {
        message.error("กรุณากำหนดราคาสินค้าอย่างน้อย 1 รายการก่อนเปิดการขาย");
      }
      if (_.isEmpty(values.category_id)) {
        message.error("กรุณาเพิ่มหมวดหมู่สินค้า");
      }
      if (_.isEmpty(values.retail_type)) {
        message.error("กรุณาเลือกประเภทร้านค้า");
      }
      return;
    }


    let new_main_images: any = [];

    if (values.main_images) {
      new_main_images = values.main_images.map((item: any) => {
        return item.id;
      });
    }

    // const new_main_images = values.main_images.map((item: any) => {
    //   return item.id;
    // });

    let new_description_images: any = [];

    if (values.description_images) {
      new_description_images = values.description_images.map((item: any) => {
        return item.id;
      });
    }

    // แปลง variants + prices เป็นโครงสร้างที่ราคาอยู่ในแต่ละ item (variants[].items[].price)
    const allItems = variants.flatMap((v) => v.items);

    // SKU ที่ผูกกับ SKU อื่นในสินค้านี้ → ตัวนั้นต้องยังอยู่และยังเป็น "จัดการสต็อกแยก" (ถูกลบ/เปลี่ยนโหมดหลังผูก = ผูกไม่ได้)
    const basicStockIds = new Set(
      allItems.filter((it: any) => it.is_stock && it.linked_stock?.is_initial_stock_sku).map((it) => it.id)
    );
    const brokenLink = allItems.find((it: any) =>
      it.is_stock &&
      it.linked_stock?.items?.some((l: any) => l.ref_client_key && !basicStockIds.has(l.ref_client_key))
    );
    if (brokenLink) {
      message.error(
        `ตัวเลือก “${brokenLink.name}” ผูกสต็อกกับตัวเลือกที่ถูกลบหรือไม่ได้ตั้ง “จัดการสต๊อกหลัก” แล้ว กรุณาแก้ไขแท็บสต็อกสินค้าก่อน`
      );
      return;
    }

    const itemsWithPrice = allItems.map((item) => {
      const price = prices.find((p: Price) => p.variant_1_item_id === item.id) || null;
      // linked_stock_draft ใช้แค่ในหน้า (ไฮเดรตแท็บคลัง) ไม่ต้องส่ง / linked_stock ส่งเฉพาะ SKU ที่เปิดสต็อก
      const { id: _id, linked_stock_draft: _draft, linked_stock, ...itemWithoutId } = item as any;
      const itemDto = {
        ...itemWithoutId,
        partner_transport_id: (item as any).partner_transport_id ?? "",
        partner_transport_brand_id: (item as any).partner_transport_brand_id ?? "",
        ...(item.is_stock && linked_stock ? { linked_stock } : {}),
        client_key: item.id, // ให้ SKU อื่นใน request อ้างถึงได้ (linked_stock.items[].ref_client_key)
      };
      const priceObj = price
        ? {
            variant_1_item_id: "",
            variant_2_item_id: price.variant_2_item_id ?? "",
            factory_price: price.factory_price,
            wholesale_price: price.wholesale_price,
            retail_price: price.retail_price,
            vip_price: price.vip_price,
            base_factory_price: price.base_factory_price,
            base_wholesale_price: price.base_wholesale_price,
            base_retail_price: price.base_retail_price,
            base_vip_price: price.base_vip_price,
            transport_price: price.transport_price ?? 0,
            api_transport_price: price.api_transport_price ?? 0,
            add_on_api_transport_price: price.add_on_api_transport_price ?? 0,
            percent_payment_gateway: price.percent_payment_gateway ?? 0,
            payment_getway: price.payment_getway ?? 0,
            commistion_lv1: price.commistion_lv1 ?? 0,
            commistion_lv2: price.commistion_lv2 ?? 0,
            commistion_lv3: price.commistion_lv3 ?? 0,
            commistion_lv4: price.commistion_lv4 ?? 0,
            commistion_lv5: price.commistion_lv5 ?? 0,
            total_commission: price.total_commission ?? 0,
            unit_factory_price: price.unit_factory_price ?? "0",
            unit_wholesale_price: price.unit_wholesale_price ?? "0",
            unit_retail_price: price.unit_retail_price ?? "0",
            unit_vip_price: price.unit_vip_price ?? "0",
            ...(price.api_transport_carrier_code != null && { api_transport_carrier_code: price.api_transport_carrier_code }),
            ...(price.api_transport_logo != null && { api_transport_logo: price.api_transport_logo }),
            ...(price.quantity_per_unit != null ? { quantity_per_unit: Number(price.quantity_per_unit) } : {}),
            // ✅ เพิ่ม: หน่วยสินค้า (uuid จาก master_all_type) — ส่งเฉพาะเมื่อมีค่า กัน null ทับค่าเดิม
            ...(price.product_unit ? { product_unit: price.product_unit } : {}),
          }
        : undefined;
      return { ...itemDto, price: priceObj };
    });

    const convertedVariantsWithPrices =
      itemsWithPrice.length > 0
        ? [
            {
              name: "ตัวเลือก",
              capacity: 100,
              items: itemsWithPrice,
            },
          ]
        : [];

    const crateProductInput: CreateProductInput = {
      name: values.name,
      recomended_retail: values.recomended_retail,
      description: values.description,
      category_id: values.category_id,
      main_images: new_main_images,
      prices: [],
      variants: convertedVariantsWithPrices as any,
      properties: attribute,
      description_images: new_description_images,
      is_active: values.is_active,
      is_recommend: values.is_recommend,
      is_dead_stock: values.is_dead_stock ?? false,
      // สินค้าที่คัดลอกมา — อ้างสินค้าต้นฉบับไว้
      ...(cloneFromId ? { parent_product_id: cloneFromId } : {}),
      //quickwin
      retail_type: values.retail_type,
      tag_list: values.tag_list,
      video_url: values.video_url,
      //sale guide
      sg_important_advice: values.sg_important_advice, // Sale guide1 คำแนะนำสำคัญ
      sg_recommended_shop: values.sg_recommended_shop,// Sale guide1 แนะนำร้านค้าเป้าหมาย
      sg_frequently_qna: values.sg_frequently_qna, // Sale guide2 คำถามที่พบบ่อยเกี่ยวกับสินค้า
      sg_sales_income: values.sg_sales_income, // Sale guide3 ส่วนของรายได้เซลล์  
      sg_invite_sale: values.sg_invite_sale, // Sale guide4 เชิญชวนเซลล์ชุมชนมาร่วมขายสินค้า
      sg_waiting_time: values.sg_waiting_time, // Sale guide5 ระยะเวลารอรับสินค้า
    };

    console.log("crateProductInput", crateProductInput);

    CreateProduct(crateProductInput, {
      onSuccess: async (data) => {
        await queryClient.refetchQueries({
          queryKey: ["getProductList"],
          type: "active",
        });
        await queryClient.refetchQueries({
          queryKey: ["getProductListInActive"],
          type: "active",
        });
        await queryClient.refetchQueries({
          queryKey: ["getProductListApprove"],
          type: "active",
        });
        navigate("/manage-product");
      },
      onError: (error) => {
        console.log("error", error);
        message.error("เกิดข้อผิดพลาด");
      },
    });
  };

  return (
    <Form form={form} onFinish={onFinish}>
      <Row className="rowgap-vbox" gutter={[24, 0]}>
        <Col span={24}>
          <Card bordered={false} className="criclebox h-full tabs-mobile">
            <HeaderTabs>
              <IconWrapper
                onClick={() => {
                  navigate("/manage-product");
                }}
              >
                <SpanIcon>{goBack}</SpanIcon>
              </IconWrapper>
              <Typography.Title level={5}>
                {cloneFromId ? "คัดลอกสินค้า" : "เพิ่มสินค้า"}
              </Typography.Title>
              <RightIconWrapper></RightIconWrapper>
            </HeaderTabs>
            <WrapperAll>
              <MainCreateProduct
                form={form}
                onFinish={onFinish}
                attribute={attribute}
                setAttribute={setAttribute}
                variants={variants}
                setVariants={setVariants}
                prices={prices}
                setPrices={setPrices}
                setCheckIsActive={setCheckIsActive}
                checkIsActive={checkIsActive}
                setCheckIsRecommend={setCheckIsRecommend}
                checkIsRecommend={checkIsRecommend}
                setCheckIsApproved={setCheckIsApproved}
                checkIsApproved={checkIsApproved}
                setCheckIsShowApp={setCheckIsShowApp}
                checkIsShowApp={checkIsShowApp}
              />
            </WrapperAll>
          </Card>
        </Col>
      </Row>
      <CreateProductFooter loading={isPending} hasSKU={variants.length > 0 && prices.length > 0} isActive={checkIsActive} />
    </Form>
  );
};

export default CreateProduct;

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  flex-direction: row;
  border-bottom: 1px solid #efefef;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #efefef;
  }
`;

const SpanIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const RightIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const WrapperAll = styled.div`
  display: block;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;
